import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import axios from "axios";
import { suggestSkills, canonicalizeSkill, prettySkill, popularSkillsForRole } from "@/data/skills";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Ordered skill chips + autosuggest input. Used by IntakePage and the landing
 * widget so the behavior is identical. Suggestions are canonicalized
 * (e.g. "springboot" -> "Spring Boot"); empty input shows popular skills.
 *
 * Props: value (string[]), onChange (string[] -> void), testId?, role?
 * When `role` is set, the empty-input "popular skills" are tailored to that
 * role's field (e.g. Sales -> Salesforce/Lead Generation, not Node.js).
 */
export default function SkillsInput({ value = [], onChange, testId = "skills-input", role = "" }) {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [roleSkills, setRoleSkills] = useState(null);  // from the scorer's taxonomy
  const blurTimer = useRef(null);

  // When a role is selected, pull the skills the SCORER expects for it (same
  // role->keyword taxonomy used by the keyword-match dimension), so the
  // suggestions are exactly what helps the score. Falls back to the local
  // category map if the request fails. Debounced; cancels stale responses.
  useEffect(() => {
    if (!role) { setRoleSkills(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      axios.get(`${API}/roles/skills`, { params: { role } })
        .then((r) => { if (!cancelled) setRoleSkills((r.data.skills || []).map(prettySkill)); })
        .catch(() => { if (!cancelled) setRoleSkills(popularSkillsForRole(role)); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [role]);

  // Typing searches the full vocabulary locally (fast). Empty input shows the
  // role-relevant skills from the backend (or local fallback / generic).
  const taken = new Set(value.map((s) => s.toLowerCase()));
  const suggestions = (!input.trim() && roleSkills && roleSkills.length)
    ? roleSkills.filter((s) => !taken.has(s.toLowerCase())).slice(0, 6)
    : suggestSkills(input, value, 6, role);

  const add = (raw) => {
    const skill = canonicalizeSkill(raw);
    if (skill && !value.some((s) => s.toLowerCase() === skill.toLowerCase())) {
      onChange([...value, skill]);
    }
    setInput("");
    setActive(0);
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      add(suggestions[active] || input);
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Backspace" && !input && value.length) {
      remove(value.length - 1);
    }
  };

  return (
    <div className="relative">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid={`${testId}-chips`}>
          {value.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-[#2832C2]/10 text-[#2832C2] text-xs font-medium px-2.5 py-1 rounded-full">
              {i + 1}. {s}
              <button type="button" onClick={() => remove(i)} className="hover:text-red-500" aria-label={`remove ${s}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        type="text" value={input}
        onChange={(e) => { setInput(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
        onKeyDown={onKeyDown}
        placeholder="Type a skill (e.g. Node.js) — Enter to add"
        className="w-full px-3 py-2.5 rounded-xl border border-[#e5e7eb] text-sm text-[#0A1128] focus:outline-none focus:border-[#2832C2]"
        data-testid={`${testId}-field`}
        autoComplete="off"
      />

      {open && suggestions.length > 0 && (
        <div
          className="absolute z-20 mt-1 w-full bg-white border border-[#e5e7eb] rounded-xl shadow-lg overflow-hidden"
          onMouseDown={() => clearTimeout(blurTimer.current)}
          data-testid={`${testId}-suggestions`}
        >
          {!input && <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400">Popular skills</div>}
          {suggestions.map((s, i) => (
            <button
              type="button" key={s}
              onClick={() => { add(s); setOpen(true); }}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-3 py-2 text-sm ${i === active ? 'bg-[#2832C2]/10 text-[#2832C2]' : 'text-[#0A1128]'}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
